import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useEffect, useState } from 'react';

import { parseNumber } from '../../utils';

type NumberInputProps = {
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly label?: string;
  readonly id: string;
  readonly tooltip?: string;
  readonly min?: number;
  readonly max?: number;
};

const clampNumber = (number: number | null, range: { min?: number; max?: number }): number | null => {
  const { min, max } = range;

  if (number === null) {
    return null;
  }

  if (min !== undefined && number < min) {
    return min;
  }

  if (max !== undefined && number > max) {
    return max;
  }

  return number;
};

export const NumberInput = (props: NumberInputProps) => {
  const { onChange, label, id, tooltip, value } = props;

  const [number, setNumber] = useState<number | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNumber(clampNumber(value, props));
  }, [props, value]);

  const clearInput = (): void => {
    setNumber(null);
    onChange(null);
  };

  const numberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newNumber = clampNumber(parseNumber(event.target.value), { min: props.min, max: props.max });
    setNumber(newNumber);
  };

  const handleEnter = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      onChange(number);
    }
  };

  return (
    <Tooltip title={tooltip ?? null}>
      <FormControl size="small" sx={{ width: '12rem' }} variant="outlined">
        <InputLabel htmlFor={id}>{label}</InputLabel>
        <OutlinedInput
          data-testid={id}
          id={id}
          value={number ?? ''}
          onChange={numberChange}
          onBlur={() => onChange(number)}
          onKeyDown={handleEnter}
          autoComplete="off"
          type="number"
          sx={{
            '& div': { display: 'none' },
            '&:hover div': { display: 'flex' },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={clearInput} edge="end">
                {number === null ? null : <CloseIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          }
          label={label}
        />
      </FormControl>
    </Tooltip>
  );
};
